/**
 * AI triage candidate types, disposition, and model provenance.
 * Algorithm/schema version is bumped when disposition payload shape changes.
 */

export const AI_TRIAGE_SCHEMA_VERSION = "ai-triage-v1.0.0";

export type AITriageCandidateCategory =
  | "seizure"
  | "spike"
  | "rhythmic_periodic"
  | "artifact"
  | "abnormal_background";

export const AI_TRIAGE_CATEGORIES: AITriageCandidateCategory[] = [
  "seizure",
  "spike",
  "rhythmic_periodic",
  "artifact",
  "abnormal_background",
];

export const AI_TRIAGE_CATEGORY_LABELS: Record<
  AITriageCandidateCategory,
  string
> = {
  seizure: "Seizure",
  spike: "Spike",
  rhythmic_periodic: "Rhythmic / periodic",
  artifact: "Artifact",
  abnormal_background: "Abnormal background",
};

export type AITriageDispositionStatus =
  | "pending"
  | "confirmed"
  | "rejected"
  | "false_positive"
  | "deferred"
  | "escalated";

export type AITriageFalsePositiveClass =
  | "artifact"
  | "normal_variant"
  | "noise"
  | "wrong_channel"
  | "timing_error"
  | "other";

export const AI_TRIAGE_FALSE_POSITIVE_LABELS: Record<
  AITriageFalsePositiveClass,
  string
> = {
  artifact: "Artifact misread as event",
  normal_variant: "Normal variant",
  noise: "Noise / poor signal",
  wrong_channel: "Wrong channel",
  timing_error: "Incorrect timing",
  other: "Other",
};

export type AITriageModelProvenance = {
  modelName: string;
  modelVersion: string;
  thresholdSetId: string;
  thresholds: Record<string, number | string | boolean>;
  inputWindow: {
    startTimeSeconds: number;
    endTimeSeconds: number;
    durationSeconds: number;
  } | null;
  preprocessingVersion: string;
  inputFeatures: string[];
  confidence: number | null;
  detectorRunId?: string | null;
};

export type AITriageModelRegistryEntry = {
  modelName: string;
  modelVersion: string;
  thresholdSetId: string;
  preprocessingVersion: string;
  intendedUse: string;
  excludedUse: string;
  status: "development" | "validation_locked" | "released" | "retired";
  validationEvidenceUri: string | null;
  approvedForDiagnosticClaims: boolean;
};

export type AITriageDisposition = {
  status: AITriageDispositionStatus;
  falsePositiveClass?: AITriageFalsePositiveClass | null;
  reviewerId?: string | null;
  reviewedAtIso?: string | null;
  notes?: string | null;
  schemaVersion: string;
};

export type AITriageAuditEntry = {
  atIso: string;
  action: AITriageDispositionStatus | "opened" | "jumped";
  reviewerId?: string | null;
  falsePositiveClass?: AITriageFalsePositiveClass | null;
  notes?: string | null;
};

export type AITriageCandidate = {
  candidateId: string;
  eventId?: string | null;
  rowVersion?: number | null;
  category: AITriageCandidateCategory;
  label: string;
  startTimeSeconds: number;
  endTimeSeconds: number | null;
  channels: string[];
  confidence: number | null;
  model: AITriageModelProvenance;
  disposition: AITriageDisposition;
  auditTrail: AITriageAuditEntry[];
  sourceKind: "algorithm" | "manual" | "import" | "system" | "local_detector";
  eventTypeCode: string;
  payload: Record<string, unknown>;
};

export const AI_TRIAGE_INTENDED_USE =
  "AI triage candidates are non-diagnostic decision-support aids that help prioritize human review of raw EEG. They do not provide standalone clinical interpretation or diagnosis.";

export const AI_TRIAGE_EXCLUDED_USE =
  "Excluded use: autonomous diagnosis, unattended alarming, closed-loop therapy, and any clinical action without qualified waveform review. Release phase remains regulatory Phase 3 gated until cleared.";

export type AITriageValidationMetrics = {
  truePositives: number;
  falseNegatives: number;
  falsePositives: number;
  recordingHours: number;
  reviewerSecondsSaved: number;
  sensitivity: number;
  falsePositivesPer24h: number;
  interRaterAgreement: number | null;
};

export function computeAITriageValidationMetrics(input: {
  truePositives: number;
  falseNegatives: number;
  falsePositives: number;
  recordingHours: number;
  reviewerSecondsSaved: number;
  /** Pairwise agreement rate 0–1 when available. */
  interRaterAgreement?: number | null;
}): AITriageValidationMetrics {
  const tp = Math.max(0, input.truePositives);
  const fn = Math.max(0, input.falseNegatives);
  const fp = Math.max(0, input.falsePositives);
  const hours = Math.max(0, input.recordingHours);
  const denom = tp + fn;
  const sensitivity = denom > 0 ? tp / denom : 0;
  const falsePositivesPer24h =
    hours > 0 ? (fp / hours) * 24 : fp > 0 ? Number.POSITIVE_INFINITY : 0;
  return {
    truePositives: tp,
    falseNegatives: fn,
    falsePositives: fp,
    recordingHours: hours,
    reviewerSecondsSaved: Math.max(0, input.reviewerSecondsSaved),
    sensitivity,
    falsePositivesPer24h,
    interRaterAgreement:
      input.interRaterAgreement == null
        ? null
        : Math.max(0, Math.min(1, input.interRaterAgreement)),
  };
}

export function emptyDisposition(
  status: AITriageDispositionStatus = "pending",
): AITriageDisposition {
  return {
    status,
    falsePositiveClass: null,
    reviewerId: null,
    reviewedAtIso: null,
    notes: null,
    schemaVersion: AI_TRIAGE_SCHEMA_VERSION,
  };
}

export function defaultModelProvenance(
  partial?: Partial<AITriageModelProvenance>,
): AITriageModelProvenance {
  const inputWindow = partial?.inputWindow
    ? {
        startTimeSeconds: partial.inputWindow.startTimeSeconds,
        endTimeSeconds: partial.inputWindow.endTimeSeconds,
        durationSeconds: Math.max(0, partial.inputWindow.durationSeconds),
      }
    : null;
  return {
    modelName: partial?.modelName || "local-rule-detector",
    modelVersion: partial?.modelVersion || "0.0.0-local",
    thresholdSetId: partial?.thresholdSetId || "local-default",
    thresholds: { ...(partial?.thresholds || {}) },
    inputWindow,
    preprocessingVersion: partial?.preprocessingVersion || "local-preprocess-v1",
    inputFeatures: [...(partial?.inputFeatures || [])],
    confidence:
      partial?.confidence == null || !Number.isFinite(partial.confidence)
        ? null
        : Math.max(0, Math.min(1, partial.confidence)),
    detectorRunId: partial?.detectorRunId ?? null,
  };
}

export function parseDispositionFromPayload(
  payload: Record<string, unknown> = {},
): AITriageDisposition {
  const raw = payload.disposition;
  if (!raw || typeof raw !== "object") return emptyDisposition("pending");
  const d = raw as Record<string, unknown>;
  const status = String(d.status || "pending") as AITriageDispositionStatus;
  const allowed: AITriageDispositionStatus[] = [
    "pending",
    "confirmed",
    "rejected",
    "false_positive",
    "deferred",
    "escalated",
  ];
  return {
    status: allowed.includes(status) ? status : "pending",
    falsePositiveClass:
      (d.falsePositiveClass as AITriageFalsePositiveClass | null) ?? null,
    reviewerId: (d.reviewerId as string | null) ?? null,
    reviewedAtIso: (d.reviewedAtIso as string | null) ?? null,
    notes: (d.notes as string | null) ?? null,
    schemaVersion: String(d.schemaVersion || AI_TRIAGE_SCHEMA_VERSION),
  };
}

export function parseModelFromPayload(
  payload: Record<string, unknown> = {},
  confidence?: number | null,
): AITriageModelProvenance {
  const raw = (payload.model || payload.detector || {}) as Record<
    string,
    unknown
  >;
  const thresholds =
    raw.thresholds && typeof raw.thresholds === "object"
      ? (raw.thresholds as Record<string, number | string | boolean>)
      : {};
  const rawWindow = (raw.inputWindow || payload.inputWindow || null) as
    | Record<string, unknown>
    | null;
  const start =
    typeof rawWindow?.startTimeSeconds === "number"
      ? rawWindow.startTimeSeconds
      : null;
  const end =
    typeof rawWindow?.endTimeSeconds === "number"
      ? rawWindow.endTimeSeconds
      : null;
  const inputFeatures = Array.isArray(raw.inputFeatures)
    ? raw.inputFeatures.map(String)
    : Array.isArray(raw.features)
      ? raw.features.map(String)
      : [];
  return defaultModelProvenance({
    modelName: String(raw.modelName || raw.name || "unknown-model"),
    modelVersion: String(raw.modelVersion || raw.version || "unknown"),
    thresholdSetId: String(raw.thresholdSetId || raw.threshold_set_id || "unknown"),
    thresholds,
    inputWindow:
      start != null && end != null
        ? {
            startTimeSeconds: start,
            endTimeSeconds: end,
            durationSeconds:
              typeof rawWindow?.durationSeconds === "number"
                ? rawWindow.durationSeconds
                : end - start,
          }
        : null,
    preprocessingVersion: String(
      raw.preprocessingVersion || raw.preprocessing_version || "unknown",
    ),
    inputFeatures,
    confidence:
      typeof confidence === "number"
        ? confidence
        : typeof raw.confidence === "number"
          ? raw.confidence
          : null,
    detectorRunId:
      (raw.detectorRunId as string | null) ??
      (payload.detectorRunId as string | null) ??
      null,
  });
}

export function parseAuditTrailFromPayload(
  payload: Record<string, unknown> = {},
): AITriageAuditEntry[] {
  const raw = payload.auditTrail;
  if (!Array.isArray(raw)) return [];
  return raw
    .filter((entry) => entry && typeof entry === "object")
    .map((entry) => {
      const e = entry as Record<string, unknown>;
      return {
        atIso: String(e.atIso || e.at || new Date(0).toISOString()),
        action: String(e.action || "opened") as AITriageAuditEntry["action"],
        reviewerId: (e.reviewerId as string | null) ?? null,
        falsePositiveClass:
          (e.falsePositiveClass as AITriageFalsePositiveClass | null) ?? null,
        notes: (e.notes as string | null) ?? null,
      };
    });
}

export function applyDispositionToPayload(
  payload: Record<string, unknown>,
  disposition: AITriageDisposition,
  auditEntry: AITriageAuditEntry,
  model?: AITriageModelProvenance,
): Record<string, unknown> {
  const prior = parseAuditTrailFromPayload(payload);
  return {
    ...payload,
    kind: payload.kind || "ai_detection",
    schemaVersion: AI_TRIAGE_SCHEMA_VERSION,
    disposition,
    model: model || parseModelFromPayload(payload),
    auditTrail: [...prior, auditEntry],
  };
}

export function buildModelRegistryEntry(input: {
  modelName: string;
  modelVersion: string;
  thresholdSetId: string;
  preprocessingVersion: string;
  status?: AITriageModelRegistryEntry["status"];
  validationEvidenceUri?: string | null;
  approvedForDiagnosticClaims?: boolean;
}): AITriageModelRegistryEntry {
  return {
    modelName: input.modelName.trim(),
    modelVersion: input.modelVersion.trim(),
    thresholdSetId: input.thresholdSetId.trim(),
    preprocessingVersion: input.preprocessingVersion.trim(),
    intendedUse: AI_TRIAGE_INTENDED_USE,
    excludedUse: AI_TRIAGE_EXCLUDED_USE,
    status: input.status || "development",
    validationEvidenceUri: input.validationEvidenceUri ?? null,
    approvedForDiagnosticClaims: Boolean(input.approvedForDiagnosticClaims),
  };
}

export function assertDiagnosticClaimAllowed(
  entry: AITriageModelRegistryEntry,
  regulatoryGateEnabled: boolean,
): void {
  if (!regulatoryGateEnabled || !entry.approvedForDiagnosticClaims) {
    throw new Error(
      "Diagnostic AI claim copy is blocked until the regulatory release gate has approved evidence.",
    );
  }
}

export function dispositionToEventStatus(
  status: AITriageDispositionStatus,
): "active" | "suppressed" {
  if (status === "rejected" || status === "false_positive") return "suppressed";
  return "active";
}

export function resolveCandidateCategory(
  eventTypeCode: string,
  payload: Record<string, unknown> = {},
): AITriageCandidateCategory {
  const explicit = payload.category || payload.candidateCategory;
  if (
    typeof explicit === "string" &&
    AI_TRIAGE_CATEGORIES.includes(explicit as AITriageCandidateCategory)
  ) {
    return explicit as AITriageCandidateCategory;
  }
  const code = eventTypeCode.toLowerCase();
  if (code.includes("seizure") || code.includes("ictal")) return "seizure";
  if (code.includes("spike") || code.includes("sharp")) return "spike";
  if (
    code.includes("rda") ||
    code.includes("pda") ||
    code.includes("rhythmic") ||
    code.includes("periodic") ||
    code.includes("grda") ||
    code.includes("gpd") ||
    code.includes("lpd")
  ) {
    return "rhythmic_periodic";
  }
  if (code.includes("artifact") || code.includes("emg") || code.includes("eog")) {
    return "artifact";
  }
  if (
    code.includes("background") ||
    code.includes("encephalopath") ||
    code.includes("slowing") ||
    code.includes("suppression")
  ) {
    return "abnormal_background";
  }
  return "spike";
}
